import { DataProcessingService } from '@/libraries/usercentrics/types';
import { SnowplowContext } from './snowplow-contexts';
import { TrackingProvider, TrackingProviderPropertiesInterface } from './tracking-provider';

interface GoogleAnalyticsConfig {
	id: string; // GA4 measurement ID, e.g., "G-XXXXXXXXXX"
	debug?: {
		enabled?: boolean;
	};
	doNotTrack: boolean;
	userId: string;
}

declare global {
	interface Window {
		gtag: (...args: any[]) => void;
	}
}

export class TrackingProviderGoogleAnalytics extends TrackingProvider {
	dataProcessingService = DataProcessingService.GOOGLE_ANALYTICS;

	constructor(private config: GoogleAnalyticsConfig) {
		super();

		if (!this.config.id) {
			throw new Error('Expected "config.id"');
		}
	}

	initialize(): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			try {
				// pre-init
				window.gtag('js', new Date());

				const script = document.createElement('script');
				script.async = true;
				script.src = `https://www.googletagmanager.com/gtag/js?id=${this.config.id}`;
				script.onload = () => {
					// starting the tracker
					window.gtag('config', this.config.id, {
						send_page_view: false, // we handle the page views ourselves
						anonymize_ip: true,
						user_id: this.config.userId || undefined,
						debug_mode: this.config.debug?.enabled || false,
					});
				};
				document.head.appendChild(script);

				resolve();
			} catch (err) {
				reject(err);
			}
		});
	}

	async trackPage({ path, title }: { path?: string; title?: string }) {
		if (this.config.doNotTrack) return;

		window.gtag('event', 'page_view', {
			page_path: path,
			page_title: title,
			user_id: this.config.userId,
		});
	}

	refreshPermanentAudiences(permanent_audiences: string[] = []) {}
	refreshPermanentAudiencesSubgenres(permanent_audiences: string[]): void {}

	async trackEvent(category: string, properties: TrackingProviderPropertiesInterface, contexts: SnowplowContext[]) {
		if (this.config.doNotTrack) return;

		window.gtag('event', properties.action || 'interaction', {
			event_category: category || 'Event',
			event_label: properties.label,
			value: properties.value,
			non_interaction: properties.nonInteraction,
			user_id: this.config.userId,
		});
	}
}
